// src/App.js
import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";

// =======================================================
// 1. PUBLIC LANDING & AUTHENTICATION SYSTEMS
// =======================================================
import Landing from "./Landing";
import Signup from "./Signup";
import Login from "./Login";
import About from "./About";
import ForgotPassword from "./ForgotPassword";
import ResetPassword from "./ResetPassword";
import VerifyEmail from "./VerifyEmail";
import BrowseInvestor from "./BrowseInvestor";
import Pricing from "./Pricing";

// =======================================================
// 2. SECURITY ROUTE HUBS (Role-Based Guards)
// =======================================================
import PrivateRoute from "./PrivateRoute";
import AdminRoute from "./components/AdminRoute";

// =======================================================
// 3. SECURED PREMIUM WORKSPACES & PROFILES
// =======================================================
import Inbox from "./Inbox"; 
import Dashboard from "./Dashboard"; 
import InvestorDashboard from "./InvestorDashboard"; 
import EditProfile from "./EditProfile"; 
import EditInvestorProfile from "./EditInvestorProfile"; 

// =======================================================
// 4. ADMINISTRATIVE PLATFORMS & AUDITS
// =======================================================
import AdminDashboard from "./AdminDashboard"; 
import AdminAnalytics from "./AdminAnalytics";

// =======================================================
// 5. DYNAMIC SEO INDEX PIPELINES & LEGAL COMPLIANCE
// =======================================================
import InvestorsCountryPage from "./Pages/InvestorsCountryPage";
import BlogPage from "./Pages/BlogPage"; 
import RedirectFundingGuide from "./Pages/RedirectFundingGuide"; 
import PrivacyPolicy from "./Pages/PrivacyPolicy";   // Paystack Compliance Path
import TermsOfService from "./Pages/TermsOfService"; // Paystack Compliance Path
import RefundPolicy from "./Pages/RefundPolicy";     // Paystack Compliance Path
import CookiePolicy from "./Pages/CookiePolicy";     // Global Cookie Compliance Path

// =======================================================
// 6. LAYOUT WRAPPERS, PRESENCE PIPELINES & ASSETS
// =======================================================
import FundingGuideLayout from "./components/FundingGuideLayout";
import CookieBanner from "./components/CookieBanner"; // Interactive Tracker Notification
import useOnlineStatus from "./Presence";
import "./App.css";
import logo from "./assets/logo.png";

// Performance-optimized Layout Wrapper with hardware-accelerated scroll spy animations
const LayoutWithLogo = ({ children }) => {
  const location = useLocation();
  const isLanding = location.pathname === "/";
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Hardware-accelerated threshold tracking
      const scrolled = window.scrollY > 40;
      setIsScrolled(scrolled);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50/30">
      {!isLanding && (
        <header className={`bg-white/90 backdrop-blur-md shadow-sm flex items-center px-6 transition-all duration-300 fixed top-0 w-full z-50 border-b border-gray-100 ${isScrolled ? "h-14" : "h-16"}`}>
          <img
            src={logo}
            alt="InvestorLanes Logo"
            className={`transition-all duration-300 transform origin-left object-contain ${isScrolled ? "h-7" : "h-9"}`}
          />
        </header>
      )}
      <main className={`flex-1 ${!isLanding ? "pt-16" : ""}`}>{children}</main>
    </div>
  );
};

// Standard fallback container matching our premium styling criteria perfectly
const NotFoundPage = () => (
  <LayoutWithLogo>
    <div className="min-h-[70vh] flex flex-col items-center justify-center text-center p-6">
      <span className="text-6xl mb-4">🔍</span>
      <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">404 - Resource Not Found</h1>
      <p className="text-gray-500 max-w-md mt-2 text-sm leading-relaxed">
        The requested regional private investment guide or allocator corridor profile is currently processing or has migrated locations.
      </p>
      <a href="/" className="mt-6 font-bold text-sm text-white px-5 py-2.5 rounded-xl bg-gray-900 hover:bg-blue-600 shadow-sm transition-all duration-200">
        Return to Global Hub
      </a>
    </div>
  </LayoutWithLogo>
);

function App() {
  useOnlineStatus();

  return (
    <Router>
      <Toaster position="top-right" toastOptions={{ duration: 4000, className: "font-medium text-sm rounded-xl shadow-md" }} />
      <Routes>
        {/* =======================================================
            1. OPEN PUBLIC GATEWAYS (Accessible by everyone)
           ======================================================= */}
        <Route path="/" element={<Landing />} />
        <Route path="/signup" element={<LayoutWithLogo><Signup /></LayoutWithLogo>} />
        <Route path="/login" element={<LayoutWithLogo><Login /></LayoutWithLogo>} />
        <Route path="/forgot-password" element={<LayoutWithLogo><ForgotPassword /></LayoutWithLogo>} />
        <Route path="/reset-password" element={<LayoutWithLogo><ResetPassword /></LayoutWithLogo>} />
        <Route path="/verify-email" element={<LayoutWithLogo><VerifyEmail /></LayoutWithLogo>} />
        <Route path="/about" element={<LayoutWithLogo><About /></LayoutWithLogo>} />
        <Route path="/pricing" element={<LayoutWithLogo><Pricing /></LayoutWithLogo>} />
        <Route path="/browse-investors" element={<LayoutWithLogo><BrowseInvestor /></LayoutWithLogo>} />

        {/* Mandatory Gatekeeper Review Frameworks */}
        <Route path="/privacy-policy" element={<LayoutWithLogo><PrivacyPolicy /></LayoutWithLogo>} />
        <Route path="/terms-of-service" element={<LayoutWithLogo><TermsOfService /></LayoutWithLogo>} />
        <Route path="/refund-policy" element={<LayoutWithLogo><RefundPolicy /></LayoutWithLogo>} />
        <Route path="/cookie-policy" element={<LayoutWithLogo><CookiePolicy /></LayoutWithLogo>} />

        {/* Dynamic SEO Indexes (Synchronized with modern component switchboards) */}
        <Route path="/investors/:countrySlug" element={<LayoutWithLogo><InvestorsCountryPage /></LayoutWithLogo>} />
        <Route path="/blog/:slug" element={<FundingGuideLayout><BlogPage /></FundingGuideLayout>} />
        <Route path="/funding-guide/:country" element={<RedirectFundingGuide />} />

        {/* =======================================================
            2. SECURED ROUTE WRAPPERS (Role-Based Guards Active)
           ======================================================= */}
        
        {/* General Workspace & Communications Security */}
        <Route path="/inbox" element={<PrivateRoute><LayoutWithLogo><Inbox /></LayoutWithLogo></PrivateRoute>} />
        <Route path="/dashboard" element={<PrivateRoute><LayoutWithLogo><Dashboard /></LayoutWithLogo></PrivateRoute>} />
        <Route path="/edit-profile" element={<PrivateRoute><LayoutWithLogo><EditProfile /></LayoutWithLogo></PrivateRoute>} />

        {/* Investor Controlled Spaces */}
        <Route path="/investor-dashboard" element={<PrivateRoute allowedRoles={["investor"]}><LayoutWithLogo><InvestorDashboard /></LayoutWithLogo></PrivateRoute>} />
        <Route path="/edit-investor-profile" element={<PrivateRoute allowedRoles={["investor"]}><LayoutWithLogo><EditInvestorProfile /></LayoutWithLogo></PrivateRoute>} />

        {/* Administrative Control Command Posts Protected by AdminRoute */}
        <Route path="/admin-dashboard" element={<AdminRoute><LayoutWithLogo><AdminDashboard /></LayoutWithLogo></AdminRoute>} />
        <Route path="/admin-analytics" element={<AdminRoute><LayoutWithLogo><AdminAnalytics /></LayoutWithLogo></AdminRoute>} />

        {/* =======================================================
            3. GSC-SAFE EXPLICIT 404 AND CATCH-ALL ROUTING NODES
           ======================================================= */}
        <Route path="/404" element={<NotFoundPage />} />
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Routes>

      {/* Global Regulatory Compliance Overlay */}
      <CookieBanner />
    </Router>
  );
}

export default App;
